import React, { useEffect, useState } from "react";
import { api, CATEGORIES, PRIORITIES, CATEGORY_STYLE } from "../lib/api";
import { toast } from "sonner";
import AdminHeader from "../components/AdminHeader";
import { Trash2, Lock, UploadCloud } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";

const EMPTY = {
  name: "", description: "", category: "General", priority: "normal",
  confirmation_required: true, resume_music: false, immediate_activation: false, is_critical: false,
};

export default function ManageAudio() {
  const [files, setFiles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/audio").then((r) => setFiles(r.data));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!form.name.trim()) { toast.error("Enter a name"); return; }
    if (!file) { toast.error("Choose an audio file"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    try {
      await api.post("/audio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Audio uploaded");
      setOpen(false); setForm(EMPTY); setFile(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.name}"? This cannot be undone.`)) return;
    await api.delete(`/audio/${f.id}`);
    toast.success("Deleted");
    load();
  };

  const toggle = async (f, field) => {
    await api.patch(`/audio/${f.id}`, { [field]: !f[field] });
    load();
  };

  const Field = ({ label, children }) => (
    <label className="block mb-3">
      <span className="block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1">{label}</span>
      {children}
    </label>
  );
  const inputCls = "w-full h-12 rounded-lg bg-[#09090B] border border-white/10 px-3 text-white outline-none focus:border-[#FF007F]";

  return (
    <div className="min-h-screen bg-[#09090B] p-4 md:p-6">
      <AdminHeader title="MANAGE AUDIO" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button data-testid="add-audio-button"
            className="mb-6 h-16 px-6 rounded-xl bg-[#FF007F] text-white font-display font-black text-lg tracking-wide flex items-center gap-3 active:scale-95 transition-transform duration-75">
            <UploadCloud className="w-7 h-7" /> UPLOAD RECORDING
          </button>
        </DialogTrigger>
        <DialogContent className="bg-[#18181B] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto yaz-scroll">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Upload Audio</DialogTitle>
            <DialogDescription className="text-[#71717A]">Add a pre-recorded announcement to the soundboard.</DialogDescription>
          </DialogHeader>
          <Field label="Name">
            <input data-testid="audio-name-input" className={inputCls} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Lockdown Announcement" />
          </Field>
          <Field label="Description">
            <input data-testid="audio-desc-input" className={inputCls} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select data-testid="audio-category-select" className={inputCls} value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select data-testid="audio-priority-select" className={inputCls} value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Audio File (MP3, WAV, M4A)">
            <input data-testid="audio-file-input" type="file" accept=".mp3,.wav,.m4a,.ogg,.aac,audio/*"
              onChange={(e) => setFile(e.target.files[0])}
              className="w-full text-white file:mr-3 file:h-10 file:px-4 file:rounded-lg file:border-0 file:bg-[#27272A] file:text-white file:font-bold" />
          </Field>
          <div className="space-y-2 mt-2">
            {[
              ["confirmation_required", "Require confirmation before playing"],
              ["immediate_activation", "Immediate activation (skip confirmation)"],
              ["resume_music", "Resume music after finishing"],
              ["is_critical", "Protected — staff cannot edit/delete"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-3 cursor-pointer">
                <input data-testid={`audio-toggle-${k}`} type="checkbox" checked={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.checked })}
                  className="w-5 h-5 accent-[#FF007F]" />
                <span className="text-sm text-white">{label}</span>
              </label>
            ))}
          </div>
          <DialogFooter className="mt-4">
            <button data-testid="submit-audio-button" onClick={upload} disabled={busy}
              className="w-full h-14 rounded-xl bg-[#FF007F] text-white font-display font-black text-lg active:scale-95 transition-transform duration-75 disabled:opacity-50">
              {busy ? "UPLOADING…" : "SAVE RECORDING"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {files.map((f) => {
          const s = CATEGORY_STYLE[f.category] || CATEGORY_STYLE.General;
          return (
            <div key={f.id} data-testid={`manage-audio-${f.id}`} className="bg-[#18181B] border border-white/10 rounded-2xl p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-1 rounded font-bold text-xs tracking-widest" style={{ background: s.bg, color: s.text }}>{s.label}</span>
                    {f.is_critical && <Lock className="w-4 h-4 text-[#FFCC00]" />}
                  </div>
                  <div className="font-display font-black text-xl text-white">{f.name}</div>
                  {f.description ? <div className="text-[#71717A] text-sm mt-1">{f.description}</div> : null}
                </div>
                <button data-testid={`delete-audio-${f.id}`} onClick={() => remove(f)}
                  className="w-11 h-11 rounded-lg bg-[#27272A] hover:bg-[#FF3B30] text-white flex items-center justify-center active:scale-95 transition-transform duration-75">
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
                {[["confirmation_required", "Confirmation"], ["resume_music", "Resume music"], ["immediate_activation", "Immediate"], ["is_critical", "Protected"]].map(([k, label]) => (
                  <button key={k} onClick={() => toggle(f, k)}
                    className={`h-10 rounded-lg font-bold text-xs tracking-wide ${f[k] ? "bg-[#00FA9A] text-black" : "bg-[#27272A] text-[#A1A1AA]"}`}>
                    {label}: {f[k] ? "ON" : "OFF"}
                  </button>
                ))}
              </div>
              <div className="text-[#52525B] text-xs mt-2 uppercase tracking-widest">Priority: {f.priority}</div>
            </div>
          );
        })}
        {files.length === 0 && <p className="text-[#71717A]">No recordings uploaded yet.</p>}
      </div>
    </div>
  );
}
